/**
 * Iter-195.B — Comparison-table CRO enhancements (T1, T2, T4, T7, T8, T10).
 *
 * Small, focused presentational helpers that augment PartnerRateTable
 * without restructuring its existing rendering paths. Each helper:
 *   - Carries a `data-testid` for regression locking.
 *   - Returns `null` when the source data isn't present so they never
 *     leave dead UI behind on rows missing the relevant fields.
 *   - Avoids any state coupling with the parent table.
 *
 * Reference: /app/memory/CONSOLIDATED_SURFACE_AUDIT_FEB2026.md §1
 * (T1, T2, T4, T5, T6, T7, T8, T10) + COMPARE_CONVERSION_AUDIT.md.
 *
 * Iter-196.QQ — ScoreHoverCard + WhyRankTooltip refactored to render
 * via `<PortalTooltip>` so the popover escapes the table's
 * `overflow-x-auto` clip and is actually visible on every row.
 */
import React from "react";
import { Sparkles, Users, ArrowUp, ArrowDown } from "lucide-react";
import PortalTooltip from "./PortalTooltip";

/* ────────────────────────────────────────────────────────────
   T1 — Score hover card
   ───────────────────────────────────────────────────────────── */
const SCORE_FIELDS = [
  { key: "score_rates", label: "Price & savings", weight: 40 },
  { key: "score_terms", label: "Terms & flexibility", weight: 25 },
  { key: "score_application", label: "Application UX", weight: 20 },
  { key: "score_support", label: "Support & claims", weight: 15 },
];

function clamp5(v) {
  return Math.max(0, Math.min(5, Number(v) || 0));
}

export function ScoreHoverCard({ rate, slug }) {
  const hasAny = SCORE_FIELDS.some((f) => rate?.[f.key] != null);
  if (!hasAny) return null;
  return (
    <PortalTooltip
      testid={`score-hover-trigger-${slug}`}
      ariaLabel="Show editor score breakdown"
      width={264}
      panelClassName="bg-white border border-slate-200 shadow-2xl p-3 text-left"
      panel={
        <>
          <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-2">
            Editor&apos;s score breakdown
          </span>
          <span className="block space-y-1.5">
            {SCORE_FIELDS.map((f) => {
              const v = rate[f.key];
              if (v == null) return null;
              const pct = clamp5(v) * 20;
              return (
                <span key={f.key} className="block">
                  <span className="flex items-baseline justify-between text-[11px]">
                    <span className="font-semibold text-slate-700">
                      {f.label}
                    </span>
                    <span className="tabular-nums text-slate-950 font-bold">
                      {clamp5(v).toFixed(1)}
                      <span className="text-slate-500 font-normal">
                        {" "}
                        · {f.weight}%
                      </span>
                    </span>
                  </span>
                  <span className="block h-1 bg-slate-100 mt-0.5">
                    <span
                      className="block h-full bg-[#10B981]"
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                </span>
              );
            })}
          </span>
        </>
      }
    />
  );
}

/* ────────────────────────────────────────────────────────────
   T2 — "Best for…" rotation
   Maps the row's dominant sub-score to a category-aware
   "Best for {audience}" tag when r.highlight isn't already set.
   ───────────────────────────────────────────────────────────── */
const BEST_FOR_BY_CATEGORY = {
  "auto-loan": {
    score_rates: "lowest-APR shoppers",
    score_terms: "long-term loans",
    score_application: "fast online approval",
    score_support: "first-time buyers",
  },
  refinance: {
    score_rates: "biggest refi savings",
    score_terms: "extended-term refi",
    score_application: "quick refi turnaround",
    score_support: "credit-rebuilders",
  },
  insurance: {
    score_rates: "budget-minded drivers",
    score_terms: "high-coverage households",
    score_application: "digital-first quoting",
    score_support: "claims-heavy drivers",
  },
  warranty: {
    score_rates: "value VSC shoppers",
    score_terms: "high-mileage vehicles",
    score_application: "no-broker direct buyers",
    score_support: "claims-paying reliability",
  },
};

export function deriveBestForLabel(rate) {
  if (!rate) return null;
  if (rate.highlight && String(rate.highlight).trim()) {
    return rate.highlight;
  }
  const map = BEST_FOR_BY_CATEGORY[rate.category] || BEST_FOR_BY_CATEGORY["auto-loan"];
  let topKey = null;
  let topVal = -1;
  for (const k of Object.keys(map)) {
    const v = Number(rate[k]);
    if (Number.isFinite(v) && v > topVal) {
      topVal = v;
      topKey = k;
    }
  }
  if (!topKey || topVal <= 0) return null;
  return `Best for ${map[topKey]}`;
}

export function BestForRotation({ rate, slug }) {
  const label = deriveBestForLabel(rate);
  if (!label) return null;
  return (
    <span
      data-testid={`best-for-rotation-${slug}`}
      className="inline-flex items-center text-[11px] font-bold uppercase tracking-[0.12em] text-[#10B981]"
    >
      {label}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────
   T4 — Coverage limits inline (insurance only)
   ───────────────────────────────────────────────────────────── */
export function CoverageLimitsInline({ details, slug }) {
  if (!details || typeof details !== "object") return null;
  // Look for the most common shapes returned by the admin schema.
  const bi =
    details.min_bi_limits ||
    details.min_bi ||
    details.bi_limits ||
    null;
  const pd = details.min_pd_limits || details.pd_limits || null;
  const um = details.um_limits || details.uninsured_motorist || null;
  const parts = [];
  if (bi) parts.push({ label: "Min BI", value: bi });
  if (pd) parts.push({ label: "PD", value: pd });
  if (um) parts.push({ label: "UM", value: um });
  if (parts.length === 0) return null;
  return (
    <span
      data-testid={`coverage-limits-inline-${slug}`}
      className="mt-1 inline-flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] tracking-[0.06em] text-slate-500"
    >
      {parts.map((p, i) => (
        <span key={p.label} className="inline-flex items-center">
          {i > 0 && <span className="mr-2 text-slate-300">·</span>}
          <span className="font-bold text-slate-700">{p.label}</span>
          <span className="ml-1 tabular-nums">{p.value}</span>
        </span>
      ))}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────
   T7 — Multi-quote callout above the table
   For non-insurance verticals (insurance already has MarketplaceStrip).
   ───────────────────────────────────────────────────────────── */
const MULTI_QUOTE_COPY = {
  "auto-loan": {
    eyebrow: "Compare 4+ lenders in one form",
    headline: "Pre-qualify with multiple lenders — soft pull only",
    cta: "See My Rates",
    sub: "4 offers · 2 minutes · won't ding your credit",
  },
  refinance: {
    eyebrow: "Compare refi lenders side-by-side",
    headline: "Multiple refi quotes — one application",
    cta: "Compare Refi Offers",
    sub: "Soft pull · No obligation · Better than your current loan or skip",
  },
  warranty: {
    eyebrow: "Compare extended warranty providers",
    headline: "See multiple VSC quotes side-by-side",
    cta: "Get VSC Quotes",
    sub: "Free quotes · No phone calls · 30-day cancellation guarantee",
  },
};

export function MultiQuoteCallout({ category, onCompare }) {
  const copy = MULTI_QUOTE_COPY[category];
  if (!copy) return null;
  return (
    <div
      data-testid={`multi-quote-callout-${category}`}
      className="mb-5 border border-[#10B981]/30 bg-[#10B981]/[0.04] p-4 lg:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span className="w-9 h-9 grid place-items-center bg-[#10B981]/10 text-[#10B981] shrink-0">
          <Users className="w-4 h-4" strokeWidth={2.5} />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981]">
            {copy.eyebrow}
          </p>
          <p className="font-display font-bold text-slate-950 text-base leading-snug mt-0.5">
            {copy.headline}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">{copy.sub}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onCompare}
        data-testid={`multi-quote-callout-cta-${category}`}
        className="shrink-0 inline-flex items-center justify-center bg-[#10B981] hover:bg-[#34D399] text-white font-bold uppercase tracking-[0.10em] text-xs px-5 py-3 transition-colors"
      >
        {copy.cta}
      </button>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   T8 — Sortable column header
   Drop-in replacement for `<th>` that adds click-to-sort UX
   and arrow indicators reflecting current sort state.
   ───────────────────────────────────────────────────────────── */
export function SortableHeader({
  id,
  label,
  align = "left",
  sortBy,
  sortDir,
  onSort,
  className = "",
  testid,
}) {
  const isActive = sortBy === id;
  const Arrow = sortDir === "desc" ? ArrowDown : ArrowUp;
  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
      ? "justify-center"
      : "justify-start";
  return (
    <th
      className={`px-5 py-4 ${className}`}
      // Iter-275.Z (a11y) — `aria-sort` is only valid on grid cells / column
      // headers per ARIA 1.2 §6.6.7. Previously set on the inner <button>
      // which axe flagged as `aria-allowed-attr` critical. Moving it to the
      // <th> (the correct landing per WAI-ARIA "Sortable Table" pattern)
      // resolves the violation without changing keyboard semantics.
      aria-sort={
        isActive ? (sortDir === "desc" ? "descending" : "ascending") : "none"
      }
    >
      <button
        type="button"
        onClick={() => onSort && onSort(id)}
        data-testid={testid || `sortable-header-${id}`}
        data-sort-active={isActive ? "true" : "false"}
        // Iter-277.A (Audit P2 #17) — Sort indicator color boost.
        // INACTIVE: opacity-40 (was opacity-0) so the affordance is
        // visible BEFORE hover. slate-900 (was slate-700) deepens the
        // contrast vs the slate-500 surrounding sort label.
        // ACTIVE: emerald background pill + ring so the active sort
        // column reads as the table's "current focus" at a glance.
        className={`group inline-flex items-center gap-1.5 ${justify} text-xs uppercase tracking-[0.10em] font-black transition-colors px-1.5 py-0.5 ${
          isActive
            ? "text-[#0A2540] bg-emerald-100 ring-1 ring-[#10B981]/40"
            : "text-slate-900 hover:text-slate-950"
        }`}
      >
        <span className="whitespace-nowrap">{label}</span>
        <Arrow
          className={`w-3 h-3 transition-opacity ${
            isActive
              ? "opacity-100"
              : "opacity-40 group-hover:opacity-80"
          }`}
          strokeWidth={2.5}
          aria-hidden
        />
      </button>
    </th>
  );
}

/* ────────────────────────────────────────────────────────────
   T10 — "Why this rank?" tooltip
   ───────────────────────────────────────────────────────────── */
function whyRankSentence(rate, rank, category) {
  if (!rate) return null;
  const bestForLabel = deriveBestForLabel(rate);
  // Choose the dominant attribute to justify the rank.
  if (rank === 1) {
    if (rate.rating && rate.rating >= 4.5) {
      return `Top-rated overall (${rate.rating.toFixed(1)}/5) by our editorial scoring.`;
    }
    if (rate.min_apr != null) {
      return `Lowest competitive APR in this category at ${rate.min_apr.toFixed(2)}%.`;
    }
    if (rate.avg_annual_premium_full != null) {
      return `Lowest verified full-coverage avg ($${Math.round(
        rate.avg_annual_premium_full / 12,
      )}/mo).`;
    }
  }
  if (rank === 2) {
    if (bestForLabel) {
      return `${bestForLabel} — second only to the rank-1 editor's pick on overall fit.`;
    }
    return "Strong second across the 4-factor scoring rubric.";
  }
  if (rank === 3) {
    if (bestForLabel) {
      return `${bestForLabel} — rounds out the top 3 with category-specific strengths.`;
    }
    return "Top-3 finalist with one or more category-leading sub-scores.";
  }
  return null;
}

export function WhyRankTooltip({ rate, rank, category, slug }) {
  if (!rank || rank < 1 || rank > 3) return null;
  const sentence = whyRankSentence(rate, rank, category);
  if (!sentence) return null;
  return (
    <PortalTooltip
      testid={`why-rank-trigger-${slug}`}
      ariaLabel="Why this rank?"
      width={224}
      panel={
        <>
          <span className="block font-black uppercase tracking-[0.16em] text-[#10B981] text-[9px] mb-1">
            Why rank #{rank}
          </span>
          <span className="block text-[11px] leading-snug">{sentence}</span>
        </>
      }
    />
  );
}

/* ────────────────────────────────────────────────────────────
   T5 — Mobile card "tap to expand" header.
   Lightweight wrapper around the card header that toggles
   expansion state. The body remains visible via the same
   `expandedSlug` state used on desktop.
   ───────────────────────────────────────────────────────────── */
export function MobileTapToExpand({ slug, isOpen, onToggle, children }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`mobile-tap-expand-${slug}`}
      aria-expanded={isOpen}
      className="w-full text-left"
    >
      {children}
      <span className="sr-only">{isOpen ? "Collapse details" : "Tap to expand details"}</span>
    </button>
  );
}

/* Sparkles re-export kept so any future call sites that import this
   helper module also get the icon they likely need. */
export { Sparkles };
